import React, { useEffect, useState } from "react";
import {
  Flex,
  Box,
  Heading,
  Button,
  Text,
  FormControl,
  FormLabel,
  Switch,
  useToast,
  SimpleGrid,
  GridItem,
  Image,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import FormInput from "../../../components/form/FormInput";
import LayoutView from "../../../components/layout";
import { useNavigate } from "react-router-dom";
import FormSelect from "../../../components/form/FromSelect";
import { Link } from "react-router-dom";
import units from "../../../utils/units";
import FormInputPassword from "../../../components/form/FormInputPassword";
import { useMutation } from "react-query";
import { registerWorkerApi } from "../../../api/auth";
import { useDropzone } from "react-dropzone";
import avatar from "../../../assets/images/avatar.png";
import camera from "../../../images/camera.png";

export default function Register() {
  const navigate = useNavigate();
  const toast = useToast();
  const [error, setError] = useState("");
  const [selectedUnit, setSselectedUnit] = useState(null);
  const [profilePreview, setProfilePreview] = useState("");

  const FormSchema = Yup.object().shape({
    idNo: Yup.string().min(3, "idNo too short").required("required"),
    firstName: Yup.string().min(3, "Name too short").required("required"),
    lastName: Yup.string().min(3, "lastName too short").required("required"),
    email: Yup.string().email("Invalid email").required("required"),
    password: Yup.string().min(6, "Password Too Short!").required("required"),
    confirmPassword: Yup.string()
      .min(6, "Confirm Password Too Short!")
      .required("required"),
    phoneNumber: Yup.string()
      .min(11, "phoneNumber too short")
      .required("required"),
    unit: Yup.string().required("required"),
    subUnit: Yup.string().required("required"),
    roomNumber: Yup.string().max(50, "Room number is too long"),
    available: Yup.boolean().required("required"),
    profileImage: Yup.mixed().required("Profile image is required"),
  });

  const registerWorkerMutation = useMutation(registerWorkerApi, {
    onSuccess: (res) => {
      toast({
        title: "Account created",
        description:
          "We've succesfully created your account, now login as a worker",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/auth/login");
    },
    onError: (err) => setError(err.response?.data?.msg),
  });

  const formik = useFormik({
    initialValues: {
      idNo: "",
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phoneNumber: "",
      unit: "",
      subUnit: "",
      roomNumber: "",
      available: true,
      profileImage: null,
    },
    onSubmit: (values) => {
      if (values.password !== values.confirmPassword) {
        return setError("Passwords does not match, please check again");
      }
      setError("");
      registerWorkerMutation.mutate(values);
    },
    validationSchema: FormSchema,
  });

  const handleUnit = (e) => {
    const unitGotten = units.find((unit) => unit.unit === e.target.value);
    setSselectedUnit(unitGotten);
    formik.handleChange("unit")(e);
  };

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept: { "image/jpeg": [], "image/png": [], "image/webp": [] },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    onDropAccepted: ([file]) => {
      formik.setFieldValue("profileImage", file);
      formik.setFieldTouched("profileImage", true, false);
      setProfilePreview((current) => {
        if (current) URL.revokeObjectURL(current);
        return URL.createObjectURL(file);
      });
    },
  });

  useEffect(() => () => {
    if (profilePreview) URL.revokeObjectURL(profilePreview);
  }, [profilePreview]);

  return (
    <LayoutView>
      <Flex align="center" justifyContent="center" minH="60vh">
        <Box
          p={8}
          maxWidth="600px"
          w="full"
          borderWidth={1}
          borderRadius={8}
          boxShadow="lg"
        >
          <Box textAlign="center">
            <Heading> Register Staff </Heading>
          </Box>
          <Box my={4} textAlign="left">
            <form onSubmit={formik.handleSubmit}>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={{ base: "4", md: "5" }}>
                <GridItem colSpan={{ base: 1, md: 2 }} textAlign="center">
                  <Box
                    {...getRootProps()}
                    mx="auto"
                    position="relative"
                    w="140px"
                    h="140px"
                    borderRadius="full"
                    borderWidth="2px"
                    borderColor={formik.touched.profileImage && formik.errors.profileImage ? "red.400" : "gray.200"}
                    cursor="pointer"
                    overflow="hidden"
                  >
                    <input {...getInputProps()} />
                    <Image src={profilePreview || avatar} alt="Staff profile" w="full" h="full" objectFit="cover" />
                    <Image src={camera} alt="Choose profile" position="absolute" top="8px" right="8px" w="28px" h="28px" />
                    {isDragActive && <Flex position="absolute" inset="0" bg="blackAlpha.600" color="white" align="center" justify="center"><Text fontWeight="700">Drop image</Text></Flex>}
                  </Box>
                  <Text mt="2" fontSize="sm" fontWeight="600">Profile image <Text as="span" color="red.500">*</Text></Text>
                  <Text fontSize="xs" color="gray.500">JPEG, PNG or WebP, up to 10MB</Text>
                  {formik.touched.profileImage && formik.errors.profileImage && <Text mt="1" color="red.500" fontSize="sm">{formik.errors.profileImage}</Text>}
                  {!!fileRejections.length && <Text mt="1" color="red.500" fontSize="sm">Select a JPEG, PNG or WebP image no larger than 10MB.</Text>}
                </GridItem>
                <FormInput
                  mt="4"
                  label="Staff ID"
                  type="text"
                  id="id"
                  placeholder="Your Staff Identification"
                  onChange={formik.handleChange("idNo")}
                  value={formik.values.idNo}
                  error={formik.touched.idNo && formik.errors.idNo}
                />
                <FormInput
                  mt="4"
                  label="First Name"
                  type="text"
                  id="firstName"
                  placeholder="Your first name"
                  onChange={formik.handleChange("firstName")}
                  value={formik.values.firstName}
                  error={formik.touched.firstName && formik.errors.firstName}
                />
                <FormInput
                  mt="4"
                  label="Last Name"
                  type="text"
                  id="lastName"
                  placeholder="Your last name"
                  onChange={formik.handleChange("lastName")}
                  value={formik.values.lastName}
                  error={formik.touched.lastName && formik.errors.lastName}
                />
                <FormInput
                  mt="4"
                  label="Email"
                  type="email"
                  id="email"
                  placeholder="test@test.com"
                  onChange={formik.handleChange("email")}
                  value={formik.values.email}
                  error={formik.touched.email && formik.errors.email}
                />
                <FormInput
                  mt="4"
                  label="Phone Number"
                  type="tel"
                  id="phone"
                  placeholder="081**********"
                  onChange={formik.handleChange("phoneNumber")}
                  value={formik.values.phoneNumber}
                  error={
                    formik.touched.phoneNumber && formik.errors.phoneNumber
                  }
                />
                <FormInputPassword
                  mt="4"
                  label="Password"
                  type="password"
                  id="password"
                  placeholder="********"
                  onChange={formik.handleChange("password")}
                  value={formik.values.password}
                  error={formik.touched.password && formik.errors.password}
                />
                <FormInputPassword
                  mt="4"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  placeholder="********"
                  onChange={formik.handleChange("confirmPassword")}
                  value={formik.values.confirmPassword}
                  error={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  }
                />
                <FormSelect
                  mt={4}
                  label="Unit"
                  options={units.map((unit) => unit.unit)}
                  placeholder="Your unit"
                  value={formik.values.unit}
                  error={formik.touched.unit && formik.errors.unit}
                  onChange={handleUnit}
                />
                {selectedUnit && (
                  <FormSelect
                    mt={4}
                    label="Sub Unit"
                    options={selectedUnit?.subUnits}
                    placeholder="Your sub unit"
                    value={formik.values.subUnit}
                    error={formik.touched.subUnit && formik.errors.subUnit}
                    onChange={formik.handleChange("subUnit")}
                  />
                )}
                <FormInput
                  mt="4"
                  label="Room Number"
                  type="text"
                  id="roomNumber"
                  placeholder="e.g. Room 12 or A-04"
                  onChange={formik.handleChange("roomNumber")}
                  value={formik.values.roomNumber}
                  error={formik.touched.roomNumber && formik.errors.roomNumber}
                />
                <FormControl display="flex" mt={4} alignItems="flex-end" mb="2">
                  <FormLabel htmlFor="isWorker" mb="0">
                    Availability status
                  </FormLabel>
                  <Switch
                    isChecked={formik.values.available}
                    onChange={formik.handleChange("available")}
                  />
                </FormControl>
              </SimpleGrid>
              <Text color="red.500" textAlign={"center"} mt="2">
                {error}
              </Text>
              <Button
                isLoading={registerWorkerMutation.isLoading}
                width="full"
                mt={2}
                type="submit"
              >
                Sign Up
              </Button>
            </form>

            <Text textAlign={"center"} fontSize={"16px"} mt="22px">
              Already have an account?{" "}
              <Text
                cursor={"pointer"}
                as="span"
                onClick={() => navigate("/auth/login")}
                color={"#19508d"}
              >
                Login here
              </Text>
            </Text>
          </Box>
        </Box>
      </Flex>
    </LayoutView>
  );
}
